import React, { lazy, Suspense, useEffect, useState } from "react";
import Loading from "src/components/Loading";
import useClient from "src/store/hooks/useClient";
import {
  endSession,
  getToken,
  isSessionEnding,
  onSessionEnd,
} from "src/utils/session";

const ServiceUnavailable = lazy(() => import("src/pages/Error"));

/** Whether the session has ended and the browser is on its way to sign in. */
export const useSessionEnding = () => {
  const [ended, setEnded] = useState(isSessionEnding);
  useEffect(() => {
    // It may have ended between the first render and this effect.
    if (isSessionEnding()) setEnded(true);
    return onSessionEnd(() => setEnded(true));
  }, []);
  return ended;
};

/**
 * Ends the session and shows that something is happening while the browser
 * leaves for the login page. Ending it from an effect rather than during the
 * render keeps rendering free of side effects.
 */
export const EndSession: React.FC = () => {
  useEffect(() => {
    endSession();
  }, []);
  return <Loading />;
};

/**
 * Nothing in the dashboard is drawn without a session.
 *
 * This replaces `if (!user.isAuthenticated) return LogOut()` in App, which
 * called a component as a function. LogOut's hooks then ran as App's own, one
 * more of them than App had rendered with while /me was loading, so React
 * threw and unmounted everything: a blank page, and the effect that was to
 * send the client to sign in never ran. That is where a /me that failed for
 * any reason other than a 401 left people.
 *
 * Now:
 * - no token in the cookie: there is nothing to refresh with, so straight to
 *   sign in, without asking for /me first;
 * - the session ended anywhere in the app (a refused request whose refresh
 *   failed, /me included): the page goes, and only the loading mark stays
 *   while the browser leaves, so no error page or half-drawn screen shows in
 *   between;
 * - /me failed for another reason before it ever answered (the gateway is
 *   down, a timeout): the session may well be fine, and sending the client to
 *   sign in would only bring them back here, so they are told the service is
 *   unavailable and offered to try again. Once /me has answered, a later
 *   refetch that fails that way keeps the page up rather than tearing it down.
 */
const SessionGate: React.FC = ({ children }) => {
  const ending = useSessionEnding();
  const client = useClient();

  if (ending || !getToken()) return <EndSession />;
  if (client.data) return <>{children}</>;
  if (client.isError)
    return (
      <Suspense fallback={<Loading />}>
        <ServiceUnavailable />
      </Suspense>
    );
  if (client.isLoading || client.isIdle) return <Loading />;
  // /me answered, but with no client in it.
  return <EndSession />;
};

export default SessionGate;
